/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 03/09/2025 - 20:02:46
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 03/09/2025
    * - Author          : 
    * - Modification    : 
**/

import React, { useState, useEffect } from "react";
import Navbar from "../../components/shared/navbar";
import Footer from "../../components/shared/footer";
import { Link } from "react-router-dom";
import { Image, Container, Row, Col, Card, Badge, Button, Form } from "react-bootstrap";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db, auth } from "../../firebase";
import images from '../../images/index';

// Define record type for better type safety
interface BillingRecord {
  id: string;
  transactionID?: string;
  amount?: number;
  projectName?: string;
  createdAt: any;
  paymentStatus?: boolean;
  invoiceUrl?: string;
  invoicePdfUrl?: string;
  voucherApplied?: string;
  originalAmount?: number;
  discountAmount?: number;
  owner?: string;
}

const Billing: React.FC = () => {
  const [data, setData] = useState<BillingRecord[]>([]);
  const [filteredData, setFilteredData] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [projectFilter, setProjectFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [projects, setProjects] = useState<string[]>([]);
  
  const user = auth.currentUser?.uid;

  // Format date function - handles different date formats
  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    
    try {
      // Handle Firestore timestamp
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      // Handle date objects
      if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      // Handle numeric timestamps
      if (typeof timestamp === 'number') {
        return new Date(timestamp).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return "Invalid date";
    }
  };
  
  // Function to properly format currency
  const formatAmount = (amount: number | undefined) => {
    if (amount === undefined || amount === null) return "€0.00";
    
    // If amount is small (less than 1), it's likely already in euros
    // If it's large, it's likely in cents and needs to be divided
    const value = amount < 100 ? amount : amount / 100;
    
    return `€${value.toFixed(2)}`;
  };
  
  // Get status badge color
  const getStatusBadgeColor = (record: BillingRecord) => {
    console.log("Getting badge color for record:", record.id, "status:", record.paymentStatus);
    // paymentStatus=true means paid
    // paymentStatus=false, null, undefined means free
    return record.paymentStatus === true ? "success" : "info"; // Paid : Free
  };
  
  // Get status text
  const getStatusText = (record: BillingRecord) => {
    console.log("Getting status text for record:", record.id, "status:", record.paymentStatus);
    // paymentStatus=true means paid
    // paymentStatus=false, null, undefined means free
    return record.paymentStatus === true ? "Paid" : "Free";
  };

  useEffect(() => {
    const fetchData = async () => {
      if (user) {
        setLoading(true);
        try {
          // Query for ALL reports
          const reportsQuery = query(
            collection(db, "paidReport"),
            where("owner", "==", user)
          );
          
          const querySnapshot = await getDocs(reportsQuery);
          const newData: BillingRecord[] = [];
          const uniqueProjects = new Set<string>();
          
          querySnapshot.docs.forEach((doc) => {
            const recordData = doc.data() as BillingRecord;
            
            // Debug - log each record to see the actual paymentStatus value
            console.log("Record:", doc.id, "paymentStatus:", recordData.paymentStatus, "type:", typeof recordData.paymentStatus);
            
            // Ensure paymentStatus is properly converted to boolean
            const normalizedRecord = {
              ...recordData,
              id: doc.id,
              // Normalize paymentStatus to be explicitly boolean
              paymentStatus: recordData.paymentStatus === true
            };
            
            // Add to unique projects list
            if (recordData.projectName) {
              uniqueProjects.add(recordData.projectName);
            }
            
            newData.push(normalizedRecord);
          });
          
          // Sort by date (newest first)
          newData.sort((a, b) => {
            const dateA = a.createdAt ? 
              (a.createdAt.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt).getTime()) : 0;
            const dateB = b.createdAt ? 
              (b.createdAt.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt).getTime()) : 0;
            return dateB - dateA;
          });
          
          console.log("Fetched data:", newData);
          setData(newData);
          setFilteredData(newData);
          setProjects(Array.from(uniqueProjects));
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [user]);
  
  // Apply filters when filter values change
  useEffect(() => {
    if (data.length === 0) return;
    
    let filtered = [...data];
    
    // Filter by project name
    if (projectFilter) {
      filtered = filtered.filter(record => 
        record.projectName === projectFilter
      );
    }
    
    // Filter by status
    if (statusFilter !== "all") {
      if (statusFilter === "paid") {
        // Paid reports (payment status true)
        filtered = filtered.filter(record => record.paymentStatus === true);
      } else if (statusFilter === "free") {
        // Free reports (payment status false or undefined)
        filtered = filtered.filter(record => record.paymentStatus !== true);
      }
    }
    
    setFilteredData(filtered);
  }, [data, projectFilter, statusFilter]);
  
  // Reset filters
  const resetFilters = () => {
    setProjectFilter("");
    setStatusFilter("all");
  };

  return (
    <div>
      <Navbar />
      
      {/* Header with Back Link */}
      <Container className="mt-4">
        <div className="d-flex align-items-center mb-4">
          <Link
            className="text-decoration-none"
            to="/dashboard"
          >
            <div className="d-flex align-items-center">
              <Image src={images.leftArrow} alt="back_arrow" width={24} />
              <span className="d-none d-sm-inline ms-2">
                Dashboard
              </span>
            </div>
          </Link>
          <h1 className="text-center flex-grow-1">Billing Details</h1>
          <div style={{ width: "24px" }}></div> {/* Spacer for alignment */}
        </div>
        
        {/* Filters Section */}
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <Row>
              <Col md={5} className="mb-3 mb-md-0">
                <Form.Group>
                  <Form.Label>Filter by Project</Form.Label>
                  <Form.Select 
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                  >
                    <option value="">All Projects</option>
                    {projects.map((project, index) => (
                      <option key={index} value={project}>{project}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Filter by Status</Form.Label>
                  <Form.Select 
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="paid">Paid Only</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={2} className="d-flex align-items-end">
                <Button 
                  variant="outline-secondary" 
                  className="w-100"
                  onClick={resetFilters}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        {/* Results Count */}
        <div className="mb-3">
          <span className="text-muted">
            Showing {filteredData.length} {filteredData.length === 1 ? 'record' : 'records'}
            {projectFilter && ` for project "${projectFilter}"`}
            {statusFilter !== 'all' && ` with status "${statusFilter}"`}
          </span>
        </div>
      </Container>
      
      {/* Loading State */}
      {loading ? (
        <Container className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading your billing records...</p>
        </Container>
      ) : filteredData.length > 0 ? (
        <Container className="mb-5">
          <Row className="g-4">
            {filteredData.map((record) => (
              <Col key={record.id} lg={4} md={6}>
                <Card className="shadow-sm h-100 hover-shadow" style={{ transition: 'box-shadow 0.3s' }}>
                  <Card.Header className="d-flex justify-content-between align-items-center py-3">
                    <h5 className="m-0 text-truncate" style={{ maxWidth: '200px' }}>
                      {record.projectName || "Solar Glare Report"}
                    </h5>
                    <Badge 
                      bg={getStatusBadgeColor(record)}
                      className="px-3 py-2"
                    >
                      {getStatusText(record)}
                    </Badge>
                  </Card.Header>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="fw-bold fs-5">Amount:</span>
                      <span className="fw-bold fs-5 text-success">
                        {formatAmount(record.amount)}
                      </span>
                    </div>
                    
                    {/* Only show voucher if voucherApplied has a value */}
                    {record.voucherApplied && (
                      <div className="d-flex justify-content-between mb-3">
                        <span className="text-muted">Original Price:</span>
                        <div>
                          <span className="text-muted">
                            <s>{formatAmount(record.originalAmount ? record.originalAmount * 100 : 1900)}</s>
                          </span>
                          <Badge bg="info" className="ms-2">Voucher</Badge>
                        </div>
                      </div>
                    )}
                    
                    <div className="d-flex justify-content-between mb-3">
                      <span className="text-muted">Date:</span>
                      <span>{formatDate(record.createdAt)}</span>
                    </div>
                    
                    {/* Only show transaction ID if it exists */}
                    {record.transactionID && (
                      <div className="mb-3">
                        <span className="text-muted d-block mb-1">Transaction ID:</span>
                        <div className="bg-light p-2 rounded text-break small">
                          {record.transactionID}
                        </div>
                      </div>
                    )}
                    
                    {/* Always show the report ID (document ID) */}
                    <div>
                    <span className="text-muted d-block mb-1">Report ID:</span>
                      <div className="bg-light p-2 rounded text-break small">
                        {record.id}
                      </div>
                    </div>
                  </Card.Body>
                  
                  {record.paymentStatus === true && record.invoiceUrl && (
                    <Card.Footer className="bg-white border-top-0">
                      <div className="d-flex gap-2">
                        <Button 
                          variant="outline-primary" 
                          className="w-100"
                          onClick={() => window.open(record.invoiceUrl, '_blank')}
                        >
                          <i className="fa fa-eye me-2"></i> View Invoice
                        </Button>
                        
                        {record.invoicePdfUrl && (
                          <Button 
                            variant="outline-success" 
                            className="w-100"
                            onClick={() => window.open(record.invoicePdfUrl, '_blank')}
                          >
                            <i className="fa fa-download me-2"></i> Download
                          </Button>
                        )}
                      </div>
                    </Card.Footer>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      ) : (
        <Container className="text-center py-5">
          <div className="py-5">
            <i className="fa fa-file-invoice fa-3x text-muted mb-3"></i>
            <h3 className="mb-3">No billing records found</h3>
            {projectFilter || statusFilter !== "all" ? (
              <p className="text-muted">
                Try adjusting your filters or <Button variant="link" className="p-0" onClick={resetFilters}>reset filters</Button>
              </p>
            ) : (
              <p className="text-muted">
                Your billing history will appear here once you make a payment
              </p>
            )}
          </div>
        </Container>
      )}
      
      <Footer />
    </div>
  );
};

export default Billing;